import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  InfoCircleOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Flex,
  Segmented,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import ReactApexChart from "react-apexcharts";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import useBranchKpis from "../../hooks/useBranchKpis";
import useCollectionBranchKpis from "../../hooks/useCollectionBranchKpis";
import useCollections from "../../hooks/useCollections";
import useDerivedMetricsV2 from "../../hooks/useDerivedMetricsV2";
import useHomeConversionV2 from "../../hooks/useHomeConversionV2";
import useHomeFiliais from "../../hooks/useHomeFiliais";
import useHomeGoalsV2 from "../../hooks/useHomeGoalsV2";
import useHomeRevenueV2 from "../../hooks/useHomeRevenueV2";
import useRScore from "../../hooks/useRScore";
import formatarDuracao from "../../utils/formatarDuracao";
import useApi from "../../utils/useApi";
import useApiV2, {
  CINCO_MINUTOS,
  repetirSoErroDeServidor,
} from "../../utils/useApiV2";
import CategoryImpactTable from "./CategoryImpactTable";
import {
  CHAVES_DERIVADAS,
  derivadasDoPeriodo,
  metaDoPeriodo,
  opcoesDeCategoria,
  periodoDerivado,
  serieDiariaDeVendas,
  serieDiariaDerivada,
  textoDasLojasFora,
  vendasDoPeriodo,
} from "./comparativoHelpers";

const { Text } = Typography;
const { RangePicker } = DatePicker;

// ─── Format helpers ───────────────────────────────────────────────────────────

const formatBRL = (v) =>
  v == null
    ? "—"
    : `R$ ${Number(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const formatBRLInt = (v) =>
  v == null ? "—" : `R$ ${Math.round(Number(v)).toLocaleString("pt-BR")}`;

const formatInt = (v) =>
  v == null ? "—" : Math.round(Number(v)).toLocaleString("pt-BR");

const formatMin = (v) => (v == null ? "—" : formatarDuracao(v));

const formatPct = (v) =>
  v == null
    ? "—"
    : `${Number(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

const formatRatio = (v) =>
  v == null
    ? "—"
    : `${(Number(v) * 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

const formatScore = (v) =>
  v == null
    ? "—"
    : Number(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });

const median = (arr) => {
  if (!arr.length) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

// ─── KPI definitions ──────────────────────────────────────────────────────────

// deltaType:
//   "pct"      → (B−A)/A × 100, shown as "±X%"
//   "abs_pp"   → B − A, shown as "±X pp" (conversao is already in %)
//   "ratio_pp" → (B−A) × 100, shown as "±X pp" (0-1 ratio → %)
const KPI_CONFIG = [
  {
    key: "faturamento_total",
    label: "Faturamento",
    tooltip: "Faturamento total conforme modo de receita selecionado",
    format: formatBRL,
    higherIsBetter: true,
    deltaType: "pct",
  },
  {
    key: "meta_atingido",
    label: "% Meta Atingido",
    tooltip: "Percentual da meta de faturamento atingido no período",
    format: formatPct,
    higherIsBetter: true,
    deltaType: "abs_pp",
  },
  {
    key: "faturamento_por_m2",
    label: "R$/m²/mês",
    tooltip:
      "Faturamento até ontem ÷ dias corridos × 30 ÷ área. No gráfico, o faturamento do dia × 30 ÷ área.",
    format: formatBRL,
    higherIsBetter: true,
    deltaType: "pct",
  },
  {
    key: "permanencia_media",
    label: "Atenção média",
    tooltip: "Tempo médio de permanência por entrada (minutos)",
    format: formatMin,
    higherIsBetter: true,
    deltaType: "pct",
  },
  {
    key: "faturamento_por_tempo_permanencia",
    label: "Faturamento/Atenção",
    tooltip:
      "Faturamento presencial dos dias com câmera por minuto total de atenção registrada",
    format: (v) => (v == null ? "—" : `${formatBRLInt(v)}/min`),
    higherIsBetter: true,
    deltaType: "pct",
  },
  {
    key: "oportunidades",
    label: "Oportunidades",
    tooltip:
      "Clientes que entraram, sem vendedores. Quem entra até 3 segundos depois do primeiro de um grupo conta junto, como uma oportunidade só.",
    format: formatInt,
    higherIsBetter: true,
    deltaType: "pct",
  },
  {
    key: "conversao",
    label: "Conversão",
    tooltip:
      "Vendas presenciais dos dias com entrada divididas pelas oportunidades (%)",
    format: formatPct,
    higherIsBetter: true,
    deltaType: "abs_pp",
  },
  {
    key: "ticket_medio",
    label: "Ticket Médio",
    tooltip: "Valor médio por transação de venda",
    format: formatBRL,
    higherIsBetter: true,
    deltaType: "pct",
  },
  {
    key: "rscore",
    label: "rSCORE",
    tooltip: "Score composto de performance (0–100)",
    format: formatScore,
    higherIsBetter: true,
    deltaType: "pct",
  },
  {
    key: "percent_vendas_estoque",
    label: "% Vendas Estoque",
    tooltip:
      "Percentual de vendas de produtos sem vinculação a zona de exposição",
    format: formatRatio,
    higherIsBetter: false,
    deltaType: "ratio_pp",
  },
];

// ─── Delta helpers ────────────────────────────────────────────────────────────

function computeDelta(a, b, deltaType) {
  if (a == null || b == null) return null;
  const numA = Number(a);
  const numB = Number(b);
  if (deltaType === "pct") {
    if (numA === 0) return null;
    return ((numB - numA) / Math.abs(numA)) * 100;
  }
  if (deltaType === "abs_pp") return numB - numA;
  if (deltaType === "ratio_pp") return (numB - numA) * 100;
  return null;
}

function DeltaBadge({ a, b, deltaType, higherIsBetter }) {
  const delta = computeDelta(a, b, deltaType);

  if (delta == null) {
    return (
      <span style={{ color: "#8c8c8c", fontSize: 12 }}>
        <MinusOutlined />
      </span>
    );
  }

  const absDelta = Math.abs(delta);
  const isPositive = delta > 0;
  const isNeutral = Math.abs(delta) < 0.05;

  const isGood = isNeutral ? null : isPositive === higherIsBetter;
  const color = isNeutral ? "#8c8c8c" : isGood ? "#52c41a" : "#ff4d4f";

  const suffix = deltaType === "pct" ? "%" : " pp";
  const sign = isPositive ? "+" : "";
  const label = `${sign}${absDelta.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}${suffix}`;

  const Icon = isNeutral
    ? MinusOutlined
    : isPositive
      ? ArrowUpOutlined
      : ArrowDownOutlined;

  return (
    <span style={{ color, fontSize: 12, whiteSpace: "nowrap" }}>
      <Icon style={{ marginRight: 2 }} />
      {label}
    </span>
  );
}

// ─── Branch select helpers ────────────────────────────────────────────────────

function buildBranchOptions(branches, isSuper, clientMap = {}) {
  const grouped = {};
  branches.forEach((b) => {
    const clientName = isSuper
      ? clientMap[b.client] || "Sem empresa"
      : b.client_name || "Sem empresa";
    const clientId = isSuper ? b.client : b.client_id;
    const key = `${clientId ?? "none"}::${clientName}`;
    if (!grouped[key]) grouped[key] = { label: clientName, options: [] };
    grouped[key].options.push({ value: b.id, label: b.name });
  });
  return Object.values(grouped).map((group) => ({
    label: <span style={{ fontWeight: 600, fontSize: 12 }}>{group.label}</span>,
    options: group.options,
  }));
}

function toApiDate(dateStr, boundary) {
  if (!dateStr) return null;
  return boundary === "start" ? `${dateStr}T00:00` : `${dateStr}T23:59`;
}

// ─── Color palette (scatter / collection) ─────────────────────────────────────

const BRANCH_COLORS = [
  "#1677ff",
  "#fa8c16",
  "#52c41a",
  "#f5222d",
  "#722ed1",
  "#13c2c2",
  "#eb2f96",
  "#a0d911",
  "#fa541c",
  "#2f54eb",
];

// ─── CollectionAnalysis ───────────────────────────────────────────────────────

function CollectionAnalysis({ collectionData, isLoading }) {
  const [sortKey, setSortKey] = useState("revenue");
  const [sortDir, setSortDir] = useState("desc");

  if (isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!collectionData?.data?.length) {
    return (
      <Text type="secondary">
        Nenhuma filial com dados para esta categoria no período.
      </Text>
    );
  }

  const raw = collectionData.data;

  const stockRefLabel = raw[0]?.stock_ref_date
    ? dayjs(raw[0].stock_ref_date).format("DD/MM/YYYY")
    : null;
  const stockInfoText = stockRefLabel
    ? `Estoque disponível (em estoque menos reservado). Fotografia de estoque de ${stockRefLabel} — não é atualizada em tempo real.`
    : "Estoque disponível (em estoque menos reservado). Sem dados de estoque para esta categoria/filial.";

  // Summary totals
  const totalRevenue = raw.reduce((s, r) => s + (r.revenue ?? 0), 0);
  const totalAttentionMin = raw.reduce((s, r) => s + (r.attention_min ?? 0), 0);
  const avgRevenuePerDay =
    raw.reduce((s, r) => s + (r.revenue_per_day ?? 0), 0) / raw.length;
  const validPen = raw.filter((r) => r.penetration_pct != null);
  const avgPenetration =
    validPen.length > 0
      ? validPen.reduce((s, r) => s + r.penetration_pct, 0) / validPen.length
      : null;

  const sorted = [...raw].sort((a, b) => {
    const va = a[sortKey] ?? -Infinity;
    const vb = b[sortKey] ?? -Infinity;
    return sortDir === "desc" ? vb - va : va - vb;
  });

  // Axis bounds — pad 25% beyond max so bubbles never clip at edges
  const xMax = Math.max(...raw.map((r) => r.attention_min ?? 0));
  const yMax = Math.max(...raw.map((r) => r.revenue ?? 0));

  // Normalize z values to [30, 80] so even the smallest bubble stays clearly visible
  const zRaw = raw.map((r) => r.revenue_per_day ?? 0);
  const zMin = Math.min(...zRaw);
  const zMax = Math.max(...zRaw);
  const normalizeZ = (v) =>
    zMax === zMin ? 55 : 30 + ((v - zMin) / (zMax - zMin)) * 50;

  const scatterSeries = raw.map((r) => ({
    name: r.branch_name,
    data: [
      {
        x: r.attention_min,
        y: r.revenue,
        z: normalizeZ(r.revenue_per_day ?? 0),
      },
    ],
  }));

  const scatterOptions = {
    chart: {
      type: "bubble",
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
    },
    dataLabels: { enabled: false },
    colors: raw.map((_, i) => BRANCH_COLORS[i % BRANCH_COLORS.length]),
    xaxis: {
      min: 0,
      max: xMax * 1.3,
      title: { text: "Exposição (min)", style: { fontSize: "12px" } },
      labels: {
        formatter: (v) =>
          Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 0 }),
      },
    },
    yaxis: {
      min: 0,
      max: yMax * 1.3,
      title: { text: "Faturamento (R$)", style: { fontSize: "12px" } },
      labels: {
        formatter: (v) => `R$ ${Math.round(v).toLocaleString("pt-BR")}`,
      },
    },
    tooltip: {
      custom: ({ seriesIndex }) => {
        const r = raw[seriesIndex];
        const fmtR = (v) =>
          v != null
            ? `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : "—";
        const fmtH = (v) =>
          v != null
            ? `R$ ${(v / 60).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/min`
            : "—";
        const fmtPen = (v) =>
          v != null
            ? `${(v * 100).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
            : "—";
        const fmtStock = (v) =>
          v != null
            ? `${v.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} un.`
            : "—";
        const daysLabel =
          r.days_on_display != null
            ? `${r.days_on_display} ${r.days_on_display === 1 ? "dia" : "dias"}`
            : "—";
        return `<div style="padding:10px;font-size:13px;line-height:1.8">
          <b>${r.branch_name}</b><br/>
          <span style="color:#8c8c8c">Faturamento:</span> ${fmtR(r.revenue)}<br/>
          <span style="color:#8c8c8c">Dias em exposição:</span> ${daysLabel}<br/>
          <span style="color:#8c8c8c">R$/dia:</span> ${fmtR(r.revenue_per_day)}<br/>
          <span style="color:#8c8c8c">Exposição:</span> ${formatarDuracao(r.attention_min)}<br/>
          <span style="color:#8c8c8c">R$/min Exposição:</span> ${fmtH(r.revenue_per_attention_h)}<br/>
          <span style="color:#8c8c8c">Penetração:</span> ${fmtPen(r.penetration_pct)}<br/>
          <span style="color:#8c8c8c">Estoque disp.:</span> ${fmtStock(r.available_stock)}
        </div>`;
      },
    },
    legend: { show: true, position: "bottom", fontSize: "12px" },
    noData: { text: "Sem dados" },
  };

  const columns = [
    { key: "branch_name", label: "Filial", numeric: false },
    { key: "revenue", label: "Faturamento", numeric: true },
    { key: "revenue_per_day", label: "R$/dia", numeric: true },
    { key: "attention_min", label: "Exposição", numeric: true },
    {
      key: "revenue_per_attention_h",
      label: "R$/min Exposição",
      numeric: true,
    },
    {
      key: "penetration_pct",
      label: "Penetração",
      numeric: true,
      info: "% do faturamento total da filial no período que veio desta categoria. Ex: 30% significa que 30% de tudo que a loja vendeu foi desta categoria.",
    },
    {
      key: "available_stock",
      label: "Estoque disp.",
      numeric: true,
      info: stockInfoText,
    },
  ];

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const colStyle = { padding: "10px 16px", borderBottom: "1px solid #f0f0f0" };
  const headerStyle = {
    ...colStyle,
    background: "#fafafa",
    fontWeight: 600,
    fontSize: 13,
    color: "rgba(0,0,0,0.65)",
    borderBottom: "1px solid #e8e8e8",
    cursor: "pointer",
    whiteSpace: "nowrap",
    userSelect: "none",
  };

  const summaryStats = [
    {
      label: "Faturamento total",
      value: `R$ ${totalRevenue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      label: "Média R$/dia",
      value: `R$ ${avgRevenuePerDay.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      label: "Exposição total",
      value: formatarDuracao(totalAttentionMin),
    },
    {
      label: "Penetração média",
      value:
        avgPenetration != null
          ? `${(avgPenetration * 100).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
          : "—",
      info: "Média simples da penetração entre filiais. Penetração = % do faturamento total da filial que veio desta categoria.",
    },
  ];

  return (
    <Flex vertical gap={24}>
      <Flex gap={12} wrap="wrap">
        {summaryStats.map((s) => (
          <div
            key={s.label}
            style={{
              flex: "1 1 160px",
              background: "#fafafa",
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              padding: "12px 16px",
            }}>
            <Flex align="center" gap={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {s.label}
              </Text>
              {s.info && (
                <Tooltip title={s.info}>
                  <InfoCircleOutlined
                    style={{ fontSize: 11, color: "#bfbfbf" }}
                  />
                </Tooltip>
              )}
            </Flex>
            <Text style={{ fontSize: 16, fontWeight: 600, display: "block" }}>
              {s.value}
            </Text>
          </div>
        ))}
      </Flex>

      <div>
        <Text
          style={{
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 4,
            display: "block",
          }}>
          {collectionData.collection_name} — Exposição vs Faturamento por Filial
        </Text>
        <Text
          type="secondary"
          style={{ fontSize: 12, marginBottom: 12, display: "block" }}>
          Tamanho dos círculos representa R$/dia. Clique na legenda para ocultar
          filiais.
        </Text>
        <ReactApexChart
          key={`scatter-${collectionData.collection_legacy_id}`}
          type="bubble"
          height={360}
          options={scatterOptions}
          series={scatterSeries}
        />
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "auto",
          }}>
          <thead>
            <tr>
              <th style={{ ...headerStyle, width: 40, textAlign: "center" }}>
                #
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{
                    ...headerStyle,
                    textAlign: col.numeric ? "right" : "left",
                  }}
                  onClick={() => handleSort(col.key)}>
                  {col.label}
                  {sortKey === col.key
                    ? sortDir === "desc"
                      ? " ↓"
                      : " ↑"
                    : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const colorIdx = raw.indexOf(r) % BRANCH_COLORS.length;
              return (
                <tr
                  key={r.branch_id}
                  style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <td
                    style={{
                      ...colStyle,
                      textAlign: "center",
                      color: "#8c8c8c",
                      fontSize: 13,
                    }}>
                    {i + 1}
                  </td>
                  <td style={colStyle}>
                    <Flex align="center" gap={8}>
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: BRANCH_COLORS[colorIdx],
                          flexShrink: 0,
                        }}
                      />
                      <Text style={{ fontSize: 13 }}>{r.branch_name}</Text>
                    </Flex>
                  </td>
                  <td style={{ ...colStyle, textAlign: "right" }}>
                    <Text style={{ fontSize: 13 }}>
                      {r.revenue != null
                        ? `R$ ${r.revenue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : "—"}
                    </Text>
                  </td>
                  <td style={{ ...colStyle, textAlign: "right" }}>
                    <Text style={{ fontSize: 13 }}>
                      {r.revenue_per_day != null
                        ? `R$ ${r.revenue_per_day.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : "—"}
                    </Text>
                  </td>
                  <td style={{ ...colStyle, textAlign: "right" }}>
                    <Text style={{ fontSize: 13 }}>
                      {r.attention_min != null
                        ? formatarDuracao(r.attention_min)
                        : "—"}
                    </Text>
                  </td>
                  <td style={{ ...colStyle, textAlign: "right" }}>
                    <Text style={{ fontSize: 13 }}>
                      {r.revenue_per_attention_h != null
                        ? `R$ ${(r.revenue_per_attention_h / 60).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/min`
                        : "—"}
                    </Text>
                  </td>
                  <td style={{ ...colStyle, textAlign: "right" }}>
                    <Text style={{ fontSize: 13 }}>
                      {r.penetration_pct != null
                        ? `${(r.penetration_pct * 100).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
                        : "—"}
                    </Text>
                  </td>
                  <td style={{ ...colStyle, textAlign: "right" }}>
                    <Text style={{ fontSize: 13 }}>
                      {r.available_stock != null
                        ? `${r.available_stock.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} un.`
                        : "—"}
                    </Text>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Flex>
  );
}

// ─── KPI chart (Filiais / Períodos drill-down) ────────────────────────────────

const RSCORE_DIMS = [
  { key: "atracao", label: "Atração" },
  { key: "conversao", label: "Conversão" },
  { key: "rentabilidade", label: "Rentabilidade" },
  { key: "engajamento", label: "Engajamento" },
  { key: "equilibrio", label: "Equilíbrio" },
  { key: "evolucao", label: "Evolução" },
];

function KpiChart({
  selectedKpi,
  labelA,
  labelB,
  dataA,
  dataB,
  dailyA,
  dailyB,
  loadingDailyA,
  loadingDailyB,
  isLoadingA,
  isLoadingB,
}) {
  const kpiConfig = KPI_CONFIG.find((k) => k.key === selectedKpi);
  if (!kpiConfig) return null;

  if (selectedKpi === "rscore") {
    if (isLoadingA || isLoadingB)
      return <Skeleton active paragraph={{ rows: 6 }} />;
    const scoresA = dataA?.rscore_scores;
    const scoresB = dataB?.rscore_scores;
    const safeScore = (obj, key) => {
      const v = obj?.[key];
      return v != null && Number.isFinite(v) ? v : 0;
    };
    const seriesA = RSCORE_DIMS.map((d) => safeScore(scoresA, d.key));
    const seriesB = RSCORE_DIMS.map((d) => safeScore(scoresB, d.key));
    const options = {
      chart: {
        type: "radar",
        toolbar: { show: false },
        animations: { enabled: false },
      },
      xaxis: { categories: RSCORE_DIMS.map((d) => d.label) },
      yaxis: { min: 0, max: 100, tickAmount: 4, show: false },
      fill: { opacity: 0.25 },
      stroke: { width: 2 },
      markers: { size: 3 },
      legend: { show: true, position: "bottom" },
      colors: ["#1677ff", "#fa8c16"],
      tooltip: {
        y: {
          formatter: (v) =>
            v == null
              ? "—"
              : v.toLocaleString("pt-BR", {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                }),
        },
      },
    };
    return (
      <div>
        <Text
          style={{
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 12,
            display: "block",
          }}>
          rSCORE — Dimensões
        </Text>
        <ReactApexChart
          key="kpi-radar"
          type="radar"
          height={360}
          options={options}
          series={[
            { name: labelA, data: seriesA },
            { name: labelB, data: seriesB },
          ]}
        />
      </div>
    );
  }

  if (selectedKpi === "percent_vendas_estoque") {
    if (isLoadingA || isLoadingB)
      return <Skeleton active paragraph={{ rows: 3 }} />;
    const rawA = dataA?.percent_vendas_estoque;
    const rawB = dataB?.percent_vendas_estoque;
    const pctA = rawA != null ? rawA * 100 : null;
    const pctB = rawB != null ? rawB * 100 : null;
    const fmtPct = (v) =>
      v == null
        ? "—"
        : `${v.toLocaleString("pt-BR", {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}%`;
    // Scale is fixed 0–100% (the metric is a percentage of total sales)
    const widthFor = (v) => {
      if (v == null) return 0;
      const clamped = Math.min(Math.max(v, 0), 100);
      return v > 0 ? Math.max(clamped, 2) : 0;
    };

    const renderRow = (label, value, color) => (
      <Flex vertical gap={4} style={{ width: "100%" }}>
        <Flex justify="space-between" align="baseline">
          <Text style={{ fontSize: 13, fontWeight: 500 }}>{label}</Text>
          <Text style={{ fontSize: 16, fontWeight: 600 }}>{fmtPct(value)}</Text>
        </Flex>
        <div
          style={{
            position: "relative",
            width: "100%",
            height: 10,
            background: "#f0f0f0",
            borderRadius: 5,
            overflow: "hidden",
          }}>
          <div
            style={{
              width: `${widthFor(value)}%`,
              height: "100%",
              background: color,
              borderRadius: 5,
              transition: "width 200ms ease",
            }}
          />
        </div>
        <Flex justify="space-between">
          <Text type="secondary" style={{ fontSize: 10 }}>
            0%
          </Text>
          <Text type="secondary" style={{ fontSize: 10 }}>
            100%
          </Text>
        </Flex>
      </Flex>
    );

    return (
      <div>
        <Flex align="center" gap={6} style={{ marginBottom: 16 }}>
          <Text style={{ fontSize: 13, fontWeight: 600 }}>
            % Vendas Estoque — comparativo de período
          </Text>
          <Tooltip title="Percentual de vendas de produtos sem vinculação a zona de exposição. Quanto menor, melhor (mais vendas vêm de produtos expostos em zonas monitoradas).">
            <InfoCircleOutlined style={{ color: "#bfbfbf", fontSize: 12 }} />
          </Tooltip>
        </Flex>
        <Flex vertical gap={16}>
          {renderRow(labelA, pctA, "#1677ff")}
          {renderRow(labelB, pctB, "#fa8c16")}
        </Flex>
      </div>
    );
  }

  if (loadingDailyA || loadingDailyB) {
    return <Skeleton active paragraph={{ rows: 4 }} />;
  }

  const allDates = dailyA?.dates || dailyB?.dates || [];
  const allRawA = dailyA?.[selectedKpi] ?? [];
  const allRawB = dailyB?.[selectedKpi] ?? [];

  // Drop days where both branches have no data
  const dates = [],
    rawA = [],
    rawB = [];
  allDates.forEach((d, i) => {
    const a = allRawA[i] ?? null;
    const b = allRawB[i] ?? null;
    if (a !== null || b !== null) {
      dates.push(d);
      rawA.push(a);
      rawB.push(b);
    }
  });

  const options = {
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
    },
    dataLabels: { enabled: false },
    stroke: { width: [2, 2], curve: "smooth" },
    markers: { size: dates.length <= 14 ? 4 : 0 },
    xaxis: {
      categories: dates.map((d) => dayjs(d).format("DD/MM")),
      tickAmount: Math.min(dates.length, 15),
      labels: { rotate: -30, style: { fontSize: "11px" } },
    },
    yaxis: {
      min: 0,
      labels: { formatter: (v) => (v == null ? "" : kpiConfig.format(v)) },
    },
    tooltip: {
      x: {
        formatter: (_, { dataPointIndex }) =>
          dayjs(dates[dataPointIndex]).format("DD/MM/YYYY"),
      },
      y: { formatter: (v) => (v == null ? "—" : kpiConfig.format(v)) },
      shared: true,
      intersect: false,
    },
    colors: ["#1677ff", "#fa8c16"],
    legend: { show: true, position: "bottom" },
    noData: { text: "Sem dados para o período" },
  };

  return (
    <div>
      <Text
        style={{
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 12,
          display: "block",
        }}>
        {kpiConfig.label} — evolução diária
      </Text>
      <ReactApexChart
        key={`kpi-line-${selectedKpi}`}
        type="line"
        height={280}
        options={options}
        series={[
          { name: labelA, data: rawA },
          { name: labelB, data: rawB },
        ]}
      />
    </div>
  );
}

// ─── Comparison table (Filiais / Períodos) ─────────────────────────────────────

function ValueCell({ value, format, isLoading }) {
  if (isLoading) {
    return <Skeleton.Input active size="small" style={{ width: 100 }} />;
  }
  return (
    <Text style={{ fontSize: 14 }}>{value == null ? "—" : format(value)}</Text>
  );
}

function ComparisonTable({
  labelA,
  labelB,
  dataA,
  dataB,
  isLoadingA,
  isLoadingB,
  selectedKpi,
  onKpiClick,
}) {
  const colStyle = {
    padding: "10px 16px",
    borderBottom: "1px solid #f0f0f0",
  };

  const headerStyle = {
    ...colStyle,
    background: "#fafafa",
    fontWeight: 600,
    fontSize: 13,
    color: "rgba(0,0,0,0.65)",
    borderBottom: "1px solid #e8e8e8",
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
        }}>
        <colgroup>
          <col style={{ width: "28%" }} />
          <col style={{ width: "26%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "34%" }} />
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...headerStyle, textAlign: "left" }}>KPI</th>
            <th style={{ ...headerStyle, textAlign: "center" }}>
              <Tooltip title={labelA}>
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                  {labelA}
                </span>
              </Tooltip>
            </th>
            <th
              style={{
                ...headerStyle,
                textAlign: "center",
                color: "rgba(0,0,0,0.4)",
              }}>
              Δ
            </th>
            <th style={{ ...headerStyle, textAlign: "center" }}>
              <Tooltip title={labelB}>
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                  {labelB}
                </span>
              </Tooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          {KPI_CONFIG.map((kpi, i) => {
            const valA = dataA?.[kpi.key] ?? null;
            const valB = dataB?.[kpi.key] ?? null;
            const isSelected = selectedKpi === kpi.key;
            const rowBg = isSelected
              ? "#e6f4ff"
              : i % 2 === 0
                ? "#ffffff"
                : "#fafafa";

            return (
              <tr
                key={kpi.key}
                style={{ background: rowBg, cursor: "pointer" }}
                onClick={() => onKpiClick(kpi.key)}>
                <td style={{ ...colStyle, textAlign: "left" }}>
                  <Flex align="center" gap={6}>
                    <Text style={{ fontSize: 13, fontWeight: 500 }}>
                      {kpi.label}
                    </Text>
                    <Tooltip title={kpi.tooltip}>
                      <InfoCircleOutlined
                        style={{ color: "#bfbfbf", fontSize: 12 }}
                      />
                    </Tooltip>
                  </Flex>
                </td>
                <td style={{ ...colStyle, textAlign: "center" }}>
                  <ValueCell
                    value={valA}
                    format={kpi.format}
                    isLoading={isLoadingA}
                  />
                </td>
                <td style={{ ...colStyle, textAlign: "center" }}>
                  {!isLoadingA && !isLoadingB ? (
                    <DeltaBadge
                      a={valA}
                      b={valB}
                      deltaType={kpi.deltaType}
                      higherIsBetter={kpi.higherIsBetter}
                    />
                  ) : (
                    <MinusOutlined style={{ color: "#d9d9d9", fontSize: 12 }} />
                  )}
                </td>
                <td style={{ ...colStyle, textAlign: "center" }}>
                  <ValueCell
                    value={valB}
                    format={kpi.format}
                    isLoading={isLoadingB}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Comparativo() {
  const { user } = useAuth();
  const { getBranches, getClients } = useApi();

  const canCompareBranches = Boolean(
    user?.is_superuser || user?.is_client_admin
  );

  // Branch list (for filiais / periodos modes)
  const [superBranches, setSuperBranches] = useState([]);
  const [clientMap, setClientMap] = useState({});
  useEffect(() => {
    if (user?.is_superuser) {
      Promise.all([getBranches(), getClients()])
        .then(([branchRes, clientRes]) => {
          setSuperBranches(branchRes?.data || []);
          const map = {};
          (clientRes?.data || []).forEach((c) => {
            map[c.id] = c.name;
          });
          setClientMap(map);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_superuser]);

  const branches = user?.is_superuser
    ? superBranches
    : user?.available_branches || [];

  const branchOptions = useMemo(
    () => buildBranchOptions(branches, Boolean(user?.is_superuser), clientMap),
    [branches, user?.is_superuser, clientMap]
  );

  const { getCollectionsList } = useApiV2();
  const { data: filiaisDoCliente } = useHomeFiliais({ enabled: true });
  const idsDoCliente = useMemo(
    () => (filiaisDoCliente?.filiais || []).map((filial) => filial.branch_id),
    [filiaisDoCliente]
  );
  const { data: colecoesDoCliente } = useQuery({
    queryKey: ["colecoes-do-cliente", user?.user_id ?? null, idsDoCliente],
    queryFn: () => getCollectionsList({ branch_id: idsDoCliente }),
    enabled: idsDoCliente.length > 0,
    staleTime: CINCO_MINUTOS,
    retry: repetirSoErroDeServidor,
  });
  const collections = useMemo(
    () => colecoesDoCliente?.colecoes || [],
    [colecoesDoCliente]
  );

  const { modoOuran, data: opcoesDoModo } = useCollections();
  const categorySource = modoOuran ? "ouran" : "erp";
  const collectionOptions = useMemo(
    () =>
      opcoesDeCategoria({
        modoOuran,
        opcoesDoModo,
        colecoesGerais: collections,
      }),
    [modoOuran, opcoesDoModo, collections]
  );

  // ── Mode + revenue ──────────────────────────────────────────────────────
  const [mode, setMode] = useState("colecoes");
  const [revenueMode, setRevenueMode] = useState("presencial");

  // ── Categorias filter state ───────────────────────────────────────────────
  const [collectionId, setCollectionId] = useState(null);
  const [collectionRange, setCollectionRange] = useState([null, null]);
  const [collectionPickerDates, setCollectionPickerDates] = useState(null);

  // ── Filiais mode state ──────────────────────────────────────────────────
  const [sharedRange, setSharedRange] = useState([null, null]);
  const [sharedPickerDates, setSharedPickerDates] = useState(null);
  const [branchIdA, setBranchIdA] = useState(null);
  const [branchIdB, setBranchIdB] = useState(null);

  // ── Períodos mode state ─────────────────────────────────────────────────
  const [sharedBranchId, setSharedBranchId] = useState(
    user?.active_branch_id ?? null
  );
  const [rangeA, setRangeA] = useState([null, null]);
  const [rangeAPickerDates, setRangeAPickerDates] = useState(null);
  const [rangeB, setRangeB] = useState([null, null]);
  const [rangeBPickerDates, setRangeBPickerDates] = useState(null);

  useEffect(() => {
    setSharedBranchId(user?.active_branch_id ?? null);
  }, [user?.active_branch_id]);

  // ── Applied state ───────────────────────────────────────────────────────
  const [applied, setApplied] = useState(null);

  useEffect(() => {
    setSelectedKpi(null);
  }, [applied]);

  const isFiltersReady = useMemo(() => {
    if (mode === "colecoes") {
      return collectionId && collectionRange[0] && collectionRange[1];
    }
    if (mode === "filiais") {
      return sharedRange[0] && sharedRange[1] && branchIdA && branchIdB;
    }
    return sharedBranchId && rangeA[0] && rangeA[1] && rangeB[0] && rangeB[1];
  }, [
    mode,
    collectionId,
    collectionRange,
    sharedRange,
    branchIdA,
    branchIdB,
    sharedBranchId,
    rangeA,
    rangeB,
  ]);

  const handleApply = () => {
    if (!isFiltersReady) return;

    if (mode === "colecoes") {
      setApplied({
        mode: "colecoes",
        revenueMode,
        collectionId,
        categorySource,
        startDate: toApiDate(collectionRange[0], "start"),
        endDate: toApiDate(collectionRange[1], "end"),
      });
      return;
    }

    const fmtDate = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "");
    const formatLabel = (branchId, range) => {
      const name = branches.find((b) => b.id === branchId)?.name || "";
      return range
        ? `${name}${name ? " · " : ""}${fmtDate(range[0])} – ${fmtDate(range[1])}`
        : name;
    };

    if (mode === "filiais") {
      setApplied({
        mode: "filiais",
        revenueMode,
        a: {
          branchId: branchIdA,
          startDate: toApiDate(sharedRange[0], "start"),
          endDate: toApiDate(sharedRange[1], "end"),
          label: formatLabel(branchIdA, null),
        },
        b: {
          branchId: branchIdB,
          startDate: toApiDate(sharedRange[0], "start"),
          endDate: toApiDate(sharedRange[1], "end"),
          label: formatLabel(branchIdB, null),
        },
      });
    } else {
      setApplied({
        mode: "periodos",
        revenueMode,
        a: {
          branchId: sharedBranchId,
          startDate: toApiDate(rangeA[0], "start"),
          endDate: toApiDate(rangeA[1], "end"),
          label: formatLabel(sharedBranchId, rangeA),
        },
        b: {
          branchId: sharedBranchId,
          startDate: toApiDate(rangeB[0], "start"),
          endDate: toApiDate(rangeB[1], "end"),
          label: formatLabel(sharedBranchId, rangeB),
        },
      });
    }
  };

  // ── Data queries (filiais / periodos) ───────────────────────────────────
  const isBranchMode =
    applied?.mode === "filiais" || applied?.mode === "periodos";

  const { data: kpisA, isLoading: loadingKpisA } = useBranchKpis({
    branchId: applied?.a?.branchId,
    startDate: applied?.a?.startDate,
    endDate: applied?.a?.endDate,
    revenueMode: applied?.revenueMode,
    enabled: isBranchMode,
  });

  const { data: kpisB, isLoading: loadingKpisB } = useBranchKpis({
    branchId: applied?.b?.branchId,
    startDate: applied?.b?.startDate,
    endDate: applied?.b?.endDate,
    revenueMode: applied?.revenueMode,
    enabled: isBranchMode,
  });

  const periodoA = periodoDerivado(applied?.a?.startDate, applied?.a?.endDate);
  const periodoB = periodoDerivado(applied?.b?.startDate, applied?.b?.endDate);

  const derivadasA = useDerivedMetricsV2({
    branchIds: applied?.a?.branchId ? [applied.a.branchId] : [],
    start: periodoA.start,
    end: periodoA.end,
    tipo: applied?.revenueMode,
    groupBy: "day",
    enabled: isBranchMode,
  });

  const derivadasB = useDerivedMetricsV2({
    branchIds: applied?.b?.branchId ? [applied.b.branchId] : [],
    start: periodoB.start,
    end: periodoB.end,
    tipo: applied?.revenueMode,
    groupBy: "day",
    enabled: isBranchMode,
  });

  const vendasA = useHomeRevenueV2({
    branchIds: applied?.a?.branchId ? [applied.a.branchId] : [],
    start: periodoA.start,
    end: periodoA.end,
    tipo: applied?.revenueMode,
    groupBy: "day",
    enabled: isBranchMode,
  });

  const vendasB = useHomeRevenueV2({
    branchIds: applied?.b?.branchId ? [applied.b.branchId] : [],
    start: periodoB.start,
    end: periodoB.end,
    tipo: applied?.revenueMode,
    groupBy: "day",
    enabled: isBranchMode,
  });

  const conversaoA = useHomeConversionV2({
    branchIds: applied?.a?.branchId ? [applied.a.branchId] : [],
    start: periodoA.start,
    end: periodoA.end,
    groupBy: "day",
    enabled: isBranchMode,
  });

  const conversaoB = useHomeConversionV2({
    branchIds: applied?.b?.branchId ? [applied.b.branchId] : [],
    start: periodoB.start,
    end: periodoB.end,
    groupBy: "day",
    enabled: isBranchMode,
  });

  const metasA = useHomeGoalsV2({
    branchIds: applied?.a?.branchId ? [applied.a.branchId] : [],
    start: periodoA.start,
    end: periodoA.end,
    tipo: applied?.revenueMode,
    enabled: isBranchMode,
  });

  const metasB = useHomeGoalsV2({
    branchIds: applied?.b?.branchId ? [applied.b.branchId] : [],
    start: periodoB.start,
    end: periodoB.end,
    tipo: applied?.revenueMode,
    enabled: isBranchMode,
  });

  const { data: rscoreA, isLoading: loadingRscoreA } = useRScore(
    applied?.a?.branchId,
    applied?.a?.startDate?.slice(0, 10),
    applied?.a?.endDate?.slice(0, 10),
    isBranchMode
  );

  const { data: rscoreB, isLoading: loadingRscoreB } = useRScore(
    applied?.b?.branchId,
    applied?.b?.startDate?.slice(0, 10),
    applied?.b?.endDate?.slice(0, 10),
    isBranchMode
  );

  const dataA = useMemo(
    () =>
      kpisA
        ? {
            ...kpisA,
            ...vendasDoPeriodo(
              vendasA.data,
              conversaoA.data,
              applied?.revenueMode
            ),
            ...metaDoPeriodo(metasA.data),
            ...derivadasDoPeriodo(derivadasA.data),
            rscore: rscoreA?.rscore_total ?? null,
            rscore_scores: rscoreA?.scores ?? null,
          }
        : null,
    [
      kpisA,
      vendasA.data,
      conversaoA.data,
      applied?.revenueMode,
      metasA.data,
      derivadasA.data,
      rscoreA,
    ]
  );

  const dataB = useMemo(
    () =>
      kpisB
        ? {
            ...kpisB,
            ...vendasDoPeriodo(
              vendasB.data,
              conversaoB.data,
              applied?.revenueMode
            ),
            ...metaDoPeriodo(metasB.data),
            ...derivadasDoPeriodo(derivadasB.data),
            rscore: rscoreB?.rscore_total ?? null,
            rscore_scores: rscoreB?.scores ?? null,
          }
        : null,
    [
      kpisB,
      vendasB.data,
      conversaoB.data,
      applied?.revenueMode,
      metasB.data,
      derivadasB.data,
      rscoreB,
    ]
  );

  const vendasCarregandoA = vendasA.isLoading || conversaoA.isLoading;
  const vendasCarregandoB = vendasB.isLoading || conversaoB.isLoading;
  const metasCarregando = metasA.isLoading || metasB.isLoading;
  const isLoadingA =
    loadingKpisA ||
    loadingRscoreA ||
    derivadasA.isLoading ||
    vendasCarregandoA ||
    metasCarregando;
  const isLoadingB =
    loadingKpisB ||
    loadingRscoreB ||
    derivadasB.isLoading ||
    vendasCarregandoB ||
    metasCarregando;

  // ── Selected KPI drill-down ─────────────────────────────────────────────
  const [selectedKpi, setSelectedKpi] = useState(null);

  const kpiDerivado = CHAVES_DERIVADAS.includes(selectedKpi);

  const dailyA = kpiDerivado
    ? serieDiariaDerivada(derivadasA.data)
    : serieDiariaDeVendas({
        revenue: vendasA.data,
        conversion: conversaoA.data,
        revenueMode: applied?.revenueMode,
        start: periodoA.start,
        end: periodoA.end,
      });
  const dailyB = kpiDerivado
    ? serieDiariaDerivada(derivadasB.data)
    : serieDiariaDeVendas({
        revenue: vendasB.data,
        conversion: conversaoB.data,
        revenueMode: applied?.revenueMode,
        start: periodoB.start,
        end: periodoB.end,
      });
  const loadingDailyA = kpiDerivado ? derivadasA.isLoading : vendasCarregandoA;
  const loadingDailyB = kpiDerivado ? derivadasB.isLoading : vendasCarregandoB;

  // ── Data queries (colecoes) ─────────────────────────────────────────────
  const { data: collectionData, isLoading: loadingCollection } =
    useCollectionBranchKpis({
      branchIds: idsDoCliente,
      categoriaId: applied?.collectionId,
      categorySource: applied?.categorySource ?? categorySource,
      startDate: applied?.startDate,
      endDate: applied?.endDate,
      revenueMode: applied?.revenueMode,
      enabled: applied?.mode === "colecoes",
    });

  // ── Render ──────────────────────────────────────────────────────────────
  const datePickerProps = {
    format: "DD/MM/YYYY",
    style: { width: 240 },
    disabledDate: (d) => d && d.isAfter(dayjs().subtract(1, "day")),
  };

  const modeOptions = [
    { label: "Categorias", value: "colecoes" },
    {
      label: "Filiais vs Filiais",
      value: "filiais",
      disabled: !canCompareBranches,
    },
    { label: "Período vs Período", value: "periodos" },
  ];

  return (
    <Flex
      vertical
      gap={24}
      style={{
        width: "100%",
        maxWidth: 960,
        margin: "0 auto",
        padding: "0 16px",
      }}>
      {/* ── Filter card ── */}
      <Card size="small">
        <Flex vertical gap={16}>
          {/* Mode + revenue mode row */}
          <Flex gap={12} align="center" wrap="wrap" justify="space-between">
            <Segmented
              options={modeOptions}
              value={mode}
              onChange={(v) => {
                if (!canCompareBranches && v === "filiais") return;
                setMode(v);
                setApplied(null);
              }}
            />
            <Select
              value={revenueMode}
              onChange={setRevenueMode}
              style={{ width: 160 }}
              options={[
                { value: "presencial", label: "Presencial" },
                { value: "total", label: "Total Líquido" },
              ]}
            />
          </Flex>

          {/* Filter inputs */}
          {mode === "colecoes" && (
            <Flex gap={12} wrap="wrap" align="flex-end">
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Categoria
                </Text>
                <Select
                  placeholder="Selecione a categoria"
                  value={collectionId}
                  onChange={setCollectionId}
                  options={collectionOptions}
                  style={{ width: 260 }}
                  showSearch
                  optionFilterProp="label"
                />
              </Flex>
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Período
                </Text>
                <RangePicker
                  {...datePickerProps}
                  value={
                    collectionPickerDates ??
                    (collectionRange[0] && collectionRange[1]
                      ? [dayjs(collectionRange[0]), dayjs(collectionRange[1])]
                      : null)
                  }
                  onCalendarChange={setCollectionPickerDates}
                  onChange={(dates) => {
                    setCollectionRange(
                      dates && dates[0] && dates[1]
                        ? [
                            dates[0].format("YYYY-MM-DD"),
                            dates[1].format("YYYY-MM-DD"),
                          ]
                        : [null, null]
                    );
                    setCollectionPickerDates(null);
                  }}
                  onOpenChange={(open) => {
                    if (open) setCollectionPickerDates([null, null]);
                  }}
                />
              </Flex>
            </Flex>
          )}

          {mode === "filiais" && (
            <Flex gap={12} wrap="wrap" align="flex-end">
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Período
                </Text>
                <RangePicker
                  {...datePickerProps}
                  value={
                    sharedPickerDates ??
                    (sharedRange[0] && sharedRange[1]
                      ? [dayjs(sharedRange[0]), dayjs(sharedRange[1])]
                      : null)
                  }
                  onCalendarChange={setSharedPickerDates}
                  onChange={(dates) => {
                    setSharedRange(
                      dates && dates[0] && dates[1]
                        ? [
                            dates[0].format("YYYY-MM-DD"),
                            dates[1].format("YYYY-MM-DD"),
                          ]
                        : [null, null]
                    );
                    setSharedPickerDates(null);
                  }}
                  onOpenChange={(open) => {
                    if (open) setSharedPickerDates([null, null]);
                  }}
                />
              </Flex>
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Filial A
                </Text>
                <Select
                  placeholder="Selecione a filial A"
                  value={branchIdA}
                  onChange={setBranchIdA}
                  options={branchOptions}
                  style={{ width: 220 }}
                  showSearch
                  optionFilterProp="label"
                />
              </Flex>
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Filial B
                </Text>
                <Select
                  placeholder="Selecione a filial B"
                  value={branchIdB}
                  onChange={setBranchIdB}
                  options={branchOptions}
                  style={{ width: 220 }}
                  showSearch
                  optionFilterProp="label"
                />
              </Flex>
            </Flex>
          )}

          {mode === "periodos" && (
            <Flex gap={12} wrap="wrap" align="flex-end">
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Filial
                </Text>
                <Select
                  placeholder="Selecione a filial"
                  value={sharedBranchId}
                  onChange={setSharedBranchId}
                  options={branchOptions}
                  style={{ width: 220 }}
                  showSearch
                  optionFilterProp="label"
                  disabled={!canCompareBranches && branches.length <= 1}
                />
              </Flex>
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Período A
                </Text>
                <RangePicker
                  {...datePickerProps}
                  value={
                    rangeAPickerDates ??
                    (rangeA[0] && rangeA[1]
                      ? [dayjs(rangeA[0]), dayjs(rangeA[1])]
                      : null)
                  }
                  onCalendarChange={setRangeAPickerDates}
                  onChange={(dates) => {
                    setRangeA(
                      dates && dates[0] && dates[1]
                        ? [
                            dates[0].format("YYYY-MM-DD"),
                            dates[1].format("YYYY-MM-DD"),
                          ]
                        : [null, null]
                    );
                    setRangeAPickerDates(null);
                  }}
                  onOpenChange={(open) => {
                    if (open) setRangeAPickerDates([null, null]);
                  }}
                />
              </Flex>
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Período B
                </Text>
                <RangePicker
                  {...datePickerProps}
                  value={
                    rangeBPickerDates ??
                    (rangeB[0] && rangeB[1]
                      ? [dayjs(rangeB[0]), dayjs(rangeB[1])]
                      : null)
                  }
                  onCalendarChange={setRangeBPickerDates}
                  onChange={(dates) => {
                    setRangeB(
                      dates && dates[0] && dates[1]
                        ? [
                            dates[0].format("YYYY-MM-DD"),
                            dates[1].format("YYYY-MM-DD"),
                          ]
                        : [null, null]
                    );
                    setRangeBPickerDates(null);
                  }}
                  onOpenChange={(open) => {
                    if (open) setRangeBPickerDates([null, null]);
                  }}
                />
              </Flex>
            </Flex>
          )}

          <Flex justify="flex-end">
            <Button
              type="primary"
              onClick={handleApply}
              disabled={!isFiltersReady}>
              Aplicar filtros
            </Button>
          </Flex>
        </Flex>
      </Card>

      {/* ── Categorias analysis ── */}
      {applied?.mode === "colecoes" && (
        <Card size="small">
          <CollectionAnalysis
            collectionData={collectionData}
            isLoading={loadingCollection}
          />
          {textoDasLojasFora(collectionData?.meta) && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message={textoDasLojasFora(collectionData?.meta)}
            />
          )}
        </Card>
      )}

      {/* ── Branch comparison table ── */}
      {isBranchMode && applied && (
        <Card size="small" styles={{ body: { padding: 0 } }}>
          <ComparisonTable
            labelA={applied.a.label}
            labelB={applied.b.label}
            dataA={dataA}
            dataB={dataB}
            isLoadingA={isLoadingA}
            isLoadingB={isLoadingB}
            selectedKpi={selectedKpi}
            onKpiClick={(key) =>
              setSelectedKpi((prev) => (prev === key ? null : key))
            }
          />
        </Card>
      )}

      {/* ── KPI chart ── */}
      {isBranchMode && applied && selectedKpi && (
        <Card size="small">
          <KpiChart
            selectedKpi={selectedKpi}
            labelA={applied.a.label}
            labelB={applied.b.label}
            dataA={dataA}
            dataB={dataB}
            dailyA={dailyA}
            dailyB={dailyB}
            loadingDailyA={loadingDailyA}
            loadingDailyB={loadingDailyB}
            isLoadingA={isLoadingA}
            isLoadingB={isLoadingB}
          />
        </Card>
      )}

      {/* ── Category / SKU variation ── */}
      {isBranchMode && applied && (
        <Card size="small">
          <CategoryImpactTable applied={applied} />
        </Card>
      )}
    </Flex>
  );
}
